import { useCallback, useMemo, useRef, useState } from 'react';
import {
  DismissibleTag,
  Dropdown,
  FilterableMultiSelect,
  OverflowMenu,
  OverflowMenuItem,
} from '@carbon/react';
import { Settings } from '@carbon/react/icons';
import moment from 'moment';
import {
  EAxisAlignment,
  EDataPointWidthMode,
  EFillPaletteMode,
  EStrokePaletteMode,
  FastColumnRenderableSeries,
  NumberRange,
  NumericAxis,
  parseColorToUIntArgb,
  TextLabelProvider,
  wrapText,
  XyDataSeries,
  type IFillPaletteProvider,
  type IPointMetadata,
  type IStrokePaletteProvider,
} from 'scichart';
import {
  SciChartView,
  type SciChartInitContext,
} from 'components/charts/scichart';
import { formatNumber } from 'utils/number';
import {
  useRegisterChartExport,
  type ChartExportDescriptor,
} from './chart-export-registry';
import { CashflowPeriodEditor } from './cashflow-period-editor';
import { categoryLabel } from './visual-summary-utils';
import type {
  CashflowPeriod,
  RollupDimensionOption,
  RollupFormat,
  RollupMetricOption,
  VisualSummaryRow,
} from './types';
import styles from './visual-summary-chart.module.scss';

/** One Group-by dropdown option — Carbon needs an `id`; the rollup key IS the id. */
interface GroupByItem {
  id: string;
  label: string;
}

/** SciChart point metadata carrying the pre-formatted data-label text for each bar. */
interface BarPointMetadata extends IPointMetadata {
  label: string;
}

/**
 * Categorical bar colors, one per rank. Sized to the `TOP_N_CAP` (25) so a full page
 * never repeats a hue (`index % length` only wraps if the cap is ever raised). The
 * palette isn't semantic — rank/length carries the meaning, not color — so the ordering
 * is arbitrary; the set is spread across the hue wheel for maximum bar-to-bar contrast.
 */
const BAR_PALETTE = [
  '#0f62fe',
  '#fa4d56',
  '#198038',
  '#ff832b',
  '#6929c4',
  '#08bdba',
  '#ee538b',
  '#b28600',
  '#4589ff',
  '#9b1d20',
  '#49cd7a',
  '#a56eff',
  '#005d5d',
  '#d12771',
  '#d2a106',
  '#002d9c',
  '#ba4e00',
  '#007d79',
  '#8a3ffc',
  '#33b1ff',
  '#6fdc8c',
  '#9f1853',
  '#570408',
  '#82cfff',
  '#8e6a00',
];

/** Per-bar pixel height + base padding (mock parity), and the min canvas height. */
const BAR_HEIGHT_PX = 32;
const CHART_BASE_PX = 80;
const CHART_MIN_PX = 280;

/**
 * Per-label-line height + row padding — the category label word-wraps to N lines, so a
 * row must be tall enough for the tallest label (else labels overlap). The row is
 * `max(BAR_HEIGHT_PX, maxLines * LABEL_LINE_PX + BAR_PADDING_PX)`.
 */
const LABEL_LINE_PX = 15;
const BAR_PADDING_PX = 12;

/** Soft character budget per category-label line before it word-wraps (SciChart `wrapText`). */
const LABEL_MAX_CHARS = 50;

/**
 * Fixed bar thickness (px). ABSOLUTE (not the default relative width), so growing a
 * row's height to fit a wrapped label widens the GAP between bars rather than the bars
 * themselves — the bar stays a constant thickness regardless of dimension count.
 */
const BAR_THICKNESS_PX = 20;

/** The BE top-N cap the chart requests (see `VISUAL_SUMMARY_LIMIT`); a full page ⇒ note. */
const TOP_N_CAP = 25;

/**
 * Formats a metric value for the axis labels + per-bar data labels: currency abbreviates
 * to the largest fitting unit ($, $K, $M, $B) so small amounts (e.g. $51K) stay legible
 * instead of collapsing to `$0.1M`; decimals show one place, integers show whole numbers.
 *
 * @param {number} value - The metric value.
 * @param {RollupFormat} format - The selected metric's format.
 * @return {string} The abbreviated display string.
 */
function formatBarValue(value: number, format: RollupFormat): string {
  if (format === 'currency') {
    const abs = Math.abs(value);
    const [divisor, suffix] =
      abs >= 1e9
        ? [1e9, 'B']
        : abs >= 1e6
          ? [1e6, 'M']
          : abs >= 1e3
            ? [1e3, 'K']
            : [1, ''];
    return `$${formatNumber({
      value: value / divisor,
      minPrecision: 0,
      maxPrecision: 1,
      includeCommas: true,
    })}${suffix}`;
  }
  if (format === 'decimal') {
    return formatNumber({
      value,
      minPrecision: 0,
      maxPrecision: 1,
      includeCommas: true,
    });
  }
  return formatNumber({
    value,
    minPrecision: 0,
    maxPrecision: 0,
    includeCommas: true,
  });
}

/**
 * The applied period as a chip label — "Apr 2026 – Dec 2030".
 *
 * @param {CashflowPeriod} period - The applied window.
 * @return {string} The month-range label.
 */
function periodChipLabel(period: CashflowPeriod): string {
  const from = moment(period.start).format('MMM YYYY');
  const to = moment(period.end).format('MMM YYYY');
  return `${from} – ${to}`;
}

export interface VisualSummaryChartProps {
  /** The ranked top-N series to plot (dimension values + the metric `value`). */
  series: VisualSummaryRow[];
  /** Loading umbrella (own fetch OR run-in-flight) — shows the SciChartView overlay. */
  isLoading: boolean;
  /** Chart Group-by — its OWN dimensions, independent of the table's. */
  groupBy: {
    options: RollupDimensionOption[];
    selected: string[];
    onChange: (keys: string[]) => void;
  };
  /**
   * Chart Metric — the full config metric list (the picker excludes `slotOnly`
   * metrics: a date isn't chartable and the BE returns null for one).
   */
  metric: {
    options: RollupMetricOption[];
    value: string;
    onChange: (key: string) => void;
  };
  /**
   * The chart's OWN applied cashflow period. There is NO summary line — the
   * `visual_summary` response carries no `cashflowPeriodSummary` (that's table-only).
   */
  period: {
    value: CashflowPeriod | null;
    onApply: (period: CashflowPeriod) => void;
    onRemove: () => void;
  };
  /** When set, this chart's SciChart surface registers for content export. */
  registerExport?: ChartExportDescriptor;
  /** An Asset as the tables name it, with its "(N of M Subtracts)" (`usePartialTracts().nameForAsset`). */
  nameForAsset: (name: string) => string;
}

/**
 * The Visual Summary chart card — a ranked horizontal-bar view of a completed calc's
 * slot economics, grouped by the chart's own dimensions and measured by a chosen
 * metric. Presentational + prop-driven (data + callbacks in; the section owns atoms +
 * `useVisualSummary`). The SciChart lifecycle is delegated to the shared `SciChartView`
 * (T13 spike conclusion): `initChart` sets up the rotated axes + column series + per-bar
 * palette ONCE; `renderData` mutates the data series imperatively on `[series, selected,
 * metricFormat]`. Horizontal bars come from rotating the axes — the category/index axis
 * is `Left` (vertical), the value axis is `Bottom` (horizontal).
 *
 * @param {VisualSummaryChartProps} props - The series + loading flag + group-by / metric / period bundles.
 * @return {JSX.Element} The Visual Summary chart card.
 */
export function VisualSummaryChart({
  series,
  isLoading,
  groupBy,
  metric,
  period,
  registerExport,
  nameForAsset,
}: VisualSummaryChartProps) {
  const [isPeriodEditorOpen, setIsPeriodEditorOpen] = useState(false);
  // Registers/unregisters this chart's image exporter as its surface mounts/unmounts;
  // a no-op when `registerExport` is absent (chart isn't exportable).
  const onExporterReady = useRegisterChartExport(registerExport);

  const {
    options: dimensionOptions,
    selected,
    onChange: onGroupByChange,
  } = groupBy;

  // The chartable metric list (a date metric isn't plottable — and the shipped BE
  // returns null for a `slotOnly` metric, so this exclusion is required, not cosmetic).
  const chartableMetrics = useMemo(
    () => metric.options.filter((option) => !option.slotOnly),
    [metric.options]
  );
  const selectedMetric = useMemo(
    () => chartableMetrics.find((option) => option.key === metric.value),
    [chartableMetrics, metric.value]
  );
  const metricFormat: RollupFormat = selectedMetric?.format ?? 'decimal';

  // Category (group) labels (`categoryLabel`), word-wrapped (SciChart's `wrapText`, whole
  // words only) so a long label breaks across lines instead of widening the axis (and
  // starving the bars); kept as an array of lines, which is how `TextLabelProvider` renders
  // a multi-line label.
  const wrappedLabels = useMemo<string[][]>(
    () =>
      series.map((row) =>
        wrapText(categoryLabel(row, nameForAsset), LABEL_MAX_CHARS)
      ),
    [series, nameForAsset]
  );
  // Tallest label (in lines) drives the per-row height so no row's label overlaps.
  const maxLabelLines = useMemo(
    () => wrappedLabels.reduce((max, lines) => Math.max(max, lines.length), 1),
    [wrappedLabels]
  );

  // Refs the stable `initChart` reads at draw time; `renderData` refreshes them so the
  // labels / colors / value-axis format track the current series + metric without
  // rebuilding the chart.
  const dataSeriesRef = useRef<XyDataSeries | null>(null);
  const valueAxisRef = useRef<NumericAxis | null>(null);
  const categoryAxisRef = useRef<NumericAxis | null>(null);
  const colorMapRef = useRef<number[]>([]);
  const metricFormatRef = useRef<RollupFormat>(metricFormat);

  const initChart = useCallback(
    ({ surface, wasmContext }: SciChartInitContext) => {
      // Category axis: holds the group labels, runs VERTICALLY (Left) → bars are
      // horizontal. NO `flippedCoordinates` here: for our Left-aligned index axis, the
      // default order puts index 0 at the TOP, so the BE's already-ranked rows (highest
      // first) read top-to-bottom. (Flipping it inverts them — smallest on top.)
      // Category (group) labels are arbitrary TEXT keyed by bar index. SciChart's
      // purpose-built `TextLabelProvider` is the idiomatic mechanism for that — the docs
      // recommend it over hand-rolling `formatLabel` (which is for value-derived
      // formatting). `renderData` replaces the whole `labels` array on each regroup; its
      // setter calls `invalidateParent`, forcing a relayout + label re-measure, so the
      // axis width tracks the current longest label instead of lagging a grouping behind
      // (the cause of a longer-than-last-grouping label spilling onto its bar).
      const categoryAxis = new NumericAxis(wasmContext, {
        axisAlignment: EAxisAlignment.Left,
        autoTicks: false,
        majorDelta: 1,
        minorDelta: 1,
        drawMajorGridLines: false,
        drawMinorGridLines: false,
        labelStyle: { fontSize: 11 },
        labelProvider: new TextLabelProvider(),
      });
      categoryAxisRef.current = categoryAxis;

      // Value axis: the metric, runs HORIZONTALLY (Bottom). `flippedCoordinates` makes it
      // increase LEFT→RIGHT (a value axis rotated onto the bottom otherwise grows
      // right→left, so bars would extend leftward from the right edge). Labels abbreviate
      // per the metric format ($X.XM etc., mock parity); the axis title = the metric name
      // (set in `renderData` since it changes with the picker).
      const valueAxis = new NumericAxis(wasmContext, {
        axisAlignment: EAxisAlignment.Bottom,
        flippedCoordinates: true,
        growBy: new NumberRange(0, 0.1),
        labelStyle: { fontSize: 11 },
      });
      valueAxis.labelProvider.formatLabel = (value) =>
        formatBarValue(value, metricFormatRef.current);
      valueAxisRef.current = valueAxis;

      surface.xAxes.add(categoryAxis);
      surface.yAxes.add(valueAxis);

      const dataSeries = new XyDataSeries(wasmContext);
      dataSeriesRef.current = dataSeries;

      // Per-bar color: return the rank's ARGB from the color map (built in `renderData`).
      const palette: IFillPaletteProvider & IStrokePaletteProvider = {
        strokePaletteMode: EStrokePaletteMode.SOLID,
        fillPaletteMode: EFillPaletteMode.SOLID,
        onAttached() {},
        onDetached() {},
        overrideStrokeArgb: (_x, _y, index) => colorMapRef.current[index],
        overrideFillArgb: (_x, _y, index) => colorMapRef.current[index],
      };
      surface.renderableSeries.add(
        new FastColumnRenderableSeries(wasmContext, {
          dataSeries,
          paletteProvider: palette,
          dataPointWidth: BAR_THICKNESS_PX,
          dataPointWidthMode: EDataPointWidthMode.Absolute,
          cornerRadius: 2, // rounded bar end (mock parity)
          // Per-bar value at the end of the bar; the abbreviated text rides each point's
          // metadata (appended in `renderData`) so it matches the axis formatting.
          dataLabels: {
            // No fontFamily: SciChart's WASM text renderer only knows fonts
            // registered via SciChartSurface.registerFont (none are), so naming
            // an unregistered family just logs a fallback warning and renders in
            // the default font anyway — matching the axis labels, which set none.
            style: { fontSize: 11 },
            color: '#161616',
            metaDataSelector: (metadata) =>
              (metadata as BarPointMetadata).label,
          },
        })
      );
    },
    []
  );

  const renderData = useCallback(
    ({ surface }: SciChartInitContext) => {
      const dataSeries = dataSeriesRef.current;
      if (!dataSeries) return;
      // Replace the whole labels array — SciChart's documented relayout trigger (the
      // setter invalidates the axis), so it re-measures and widens to fit the longest
      // current label. `wrappedLabels` is already bar-index-ordered; each entry is the
      // `string[]` of word-wrapped lines, which `TextLabelProvider` renders multi-line.
      if (categoryAxisRef.current) {
        (categoryAxisRef.current.labelProvider as TextLabelProvider).labels =
          wrappedLabels;
      }
      colorMapRef.current = series.map((_, index) =>
        parseColorToUIntArgb(BAR_PALETTE[index % BAR_PALETTE.length])
      );
      metricFormatRef.current = metricFormat;
      if (valueAxisRef.current) {
        valueAxisRef.current.axisTitle = selectedMetric?.label ?? '';
      }
      // Per-point metadata carries the abbreviated data-label text, read back by the
      // series' `metaDataSelector`.
      const metadata: BarPointMetadata[] = series.map((row) => ({
        isSelected: false,
        label: formatBarValue(row.value, metricFormat),
      }));
      dataSeries.clear();
      dataSeries.appendRange(
        series.map((_, index) => index), // x = rank index 0..n-1
        series.map((row) => row.value), // y = metric value
        metadata
      );
      surface.zoomExtents();
      // Frame the bar (category) axis to exactly one row per bar with a symmetric
      // half-row margin top + bottom — the minimal padding. This pins the vertical
      // extent to the bar count (overriding zoomExtents' index framing), so with few
      // bars the chart no longer leaves large empty bands above/below them.
      if (categoryAxisRef.current && series.length > 0) {
        categoryAxisRef.current.visibleRange = new NumberRange(
          -0.5,
          series.length - 0.5
        );
      }
    },
    [series, wrappedLabels, metricFormat, selectedMetric]
  );

  const groupByItems = useMemo<GroupByItem[]>(
    () =>
      dimensionOptions.map((option) => ({
        id: option.key,
        label: option.label,
      })),
    [dimensionOptions]
  );
  const selectedGroupByItems = useMemo(
    () => groupByItems.filter((item) => selected.includes(item.id)),
    [groupByItems, selected]
  );
  const handleGroupBySelection = useCallback(
    ({ selectedItems }: { selectedItems: GroupByItem[] }) =>
      onGroupByChange(selectedItems.map((item) => item.id)),
    [onGroupByChange]
  );

  const periodItemText = period.value
    ? 'Edit Cashflow Period'
    : 'Add Cashflow Period';

  // Grow each bar's row so the tallest word-wrapped label has room; the canvas height is
  // DRIVEN BY THE BAR COUNT (+ the fixed bottom axis/title chrome) — so few bars yield a
  // short chart, not thin bars floating in a tall one. `CHART_MIN_PX` is only the
  // empty/loading floor (no bars to size to); the vertical padding around the bars is the
  // half-row margin the category axis is framed with in `renderData`.
  const rowHeightPx = Math.max(
    BAR_HEIGHT_PX,
    maxLabelLines * LABEL_LINE_PX + BAR_PADDING_PX
  );
  const chartHeight = series.length
    ? series.length * rowHeightPx + CHART_BASE_PX
    : CHART_MIN_PX;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <FilterableMultiSelect<GroupByItem>
          id="visual-summary-group-by"
          className={styles.groupBy}
          aria-label="Group by dimensions"
          placeholder="Group by dimensions…"
          size="lg"
          items={groupByItems}
          itemToString={(item) => item?.label ?? ''}
          selectedItems={selectedGroupByItems}
          onChange={handleGroupBySelection}
        />
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Metric</span>
          <Dropdown<RollupMetricOption>
            id="visual-summary-metric"
            className={styles.metricDropdown}
            size="lg"
            titleText="Metric"
            hideLabel
            label="Select a metric"
            items={chartableMetrics}
            itemToString={(item) => item?.label ?? ''}
            selectedItem={selectedMetric}
            onChange={({ selectedItem }) =>
              selectedItem && metric.onChange(selectedItem.key)
            }
          />
        </div>
        <OverflowMenu
          aria-label="Chart options"
          renderIcon={Settings}
          size="lg"
          flipped
        >
          <OverflowMenuItem
            className={styles.gearItem}
            itemText={periodItemText}
            onClick={() => setIsPeriodEditorOpen((open) => !open)}
          />
        </OverflowMenu>
      </div>

      {isPeriodEditorOpen && (
        // Apply / Remove act in place and KEEP the editor open; only Cancel dismisses it.
        <CashflowPeriodEditor
          open
          value={period.value}
          onApply={period.onApply}
          onCancel={() => setIsPeriodEditorOpen(false)}
          onRemove={period.onRemove}
        />
      )}

      {(selected.length > 0 || period.value) && (
        <div className={styles.chips}>
          {selected.map((key) => {
            const option = dimensionOptions.find((entry) => entry.key === key);
            return (
              <DismissibleTag
                key={key}
                type="gray"
                text={option?.label ?? key}
                dismissTooltipLabel="Remove dimension"
                onClose={() =>
                  onGroupByChange(selected.filter((entry) => entry !== key))
                }
              />
            );
          })}
          {period.value && (
            <DismissibleTag
              type="teal"
              text={`Period: ${periodChipLabel(period.value)}`}
              dismissTooltipLabel="Remove cashflow period"
              onClose={period.onRemove}
            />
          )}
        </div>
      )}

      <SciChartView
        initChart={initChart}
        renderData={renderData}
        renderDeps={[series, wrappedLabels, metricFormat, selectedMetric]}
        isLoading={isLoading}
        height={chartHeight}
        onExporterReady={onExporterReady}
      >
        {series.length === 0 && !isLoading && (
          <p className={styles.emptyMsg}>No data to chart.</p>
        )}
      </SciChartView>

      {series.length >= TOP_N_CAP && (
        <p className={styles.chartNote}>
          {`Showing top ${TOP_N_CAP} groups${
            selectedMetric ? ` by ${selectedMetric.label}` : ''
          }.`}
        </p>
      )}
    </div>
  );
}
